<script setup>
import { ref } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useCommentRowCells } from '@/composables/document-row'
import { useNotify } from '@/composables/notify'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import InvoiceCommentEdit from '@/components/accounting/InvoiceCommentEdit.vue'

const props = defineProps({
  invoice: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['row-saved', 'add-rows'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()

const { gridRef } = dataGrid.useGridRef()
const { isCommentRow, clearCommentRowPricingCell, COMMENT_ROW_PRICING_FIELDS } = useCommentRowCells()
const { onDragEnd } = dataGrid.useDefaultRowReordering(accountingStore.api.reorderInvoiceRowsAsync, props.invoice.id, gridRef)

const { notifySuccess } = useNotify()

const showCommentEdit = ref(false)
const selectedRowIds = ref([])
const showBulkDeleteConfirm = ref(false)
let deleteSelectedButton = null

const isDraft = props.invoice.status === accountingStore.invoiceStatusEnum.DRAFT
const currency = webSiteStore.currencies.find(item => item.id === props.invoice.currency_ref)

planningStore.fetchProjectsAsync({}, true)

const dataSource = dataGrid.useODataSource(accountingStore.api.getAbsoluteUrl() + accountingStore.api.invoiceRowsUrl)
dataSource.filter(['invoice', '=', props.invoice.id])

const columns = [
  {
    type: 'buttons',
    buttons: ['delete'],
    visible: isDraft,
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '3rem',
  },
  dataGridColumn.useRowNumberColumn({ allowEditing: false }),
  dataGridColumn.useProjectColumn({ allowEditing: false, visible: false }),
  dataGridColumn.useInvoiceRowSourceColumn(),
  { dataField: 'listing_item_text', caption: i18n.t('common.listingItem'), allowEditing: false },
  dataGridColumn.useDescriptionColumn(),
  { dataField: 'date', caption: i18n.t('common.date'), dataType: 'date', visible: false },
  dataGridColumn.useQuantityColumn(),
  dataGridColumn.useUnitOfMeasureColumn(),
  { dataField: 'amount_per_unit', caption: i18n.t('common.plannedPerUnitAlt'), dataType: 'number', format: '#0.##', width: '5rem', allowEditing: false },
  dataGridColumn.usePriceColumn({ currency }),
  dataGridColumn.useChargePercentageColumn(),
  dataGridColumn.useDiscountColumn(),
  dataGridColumn.useVatTypeColumn(),
  dataGridColumn.useTotalNoVatColumn({ currency }),
  dataGridColumn.useTotalVatColumn({ currency }),
  dataGridColumn.useNotesColumn(),
]

const onSelectionChanged = (e) => {
  selectedRowIds.value = e.selectedRowKeys
  deleteSelectedButton?.option('disabled', selectedRowIds.value.length === 0)
}

const onBulkDeleteConfirm = async () => {
  showBulkDeleteConfirm.value = false
  await accountingStore.api.bulkDeleteInvoiceRowsAsync(props.invoice.id, selectedRowIds.value)
  notifySuccess(i18n.t('info.deletedSuccessfully'))
  emit('row-saved', props.invoice.id)
  gridRef.value?.instance.clearSelection()
  await gridRef.value?.instance.refresh()
}

const deleteSelectedToolbarItem = {
  widget: 'dxButton',
  location: 'after',
  visible: isDraft,
  options: {
    icon: 'trash',
    hint: i18n.t('common.deleteSelected'),
    disabled: true,
    onClick: () => { showBulkDeleteConfirm.value = true },
    onInitialized: (e) => { deleteSelectedButton = e.component },
  },
}

const onCommentAdded = async (rowId) => {
  showCommentEdit.value = false
  emit('row-saved', props.invoice.id)
  await dataGrid.refreshGridAsync(gridRef, rowId)
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: `invoice-rows-grid-${props.invoice.id}`,
  saveStateId: 'invoice-rows-grid',
  dataSource,
  columns,
  isDetailGrid: true,
  editing: { mode: 'cell', allowUpdating: isDraft, allowDeleting: isDraft, allowAdding: false, useIcons: true },
  allowAdding: isDraft,
  onEditingStart: (e) => { e.cancel = isCommentRow(e.data) && COMMENT_ROW_PRICING_FIELDS.includes(e.column?.dataField) },
  onSaved: () => emit('row-saved', props.invoice.id),
  onRowRemoved: () => emit('row-saved', props.invoice.id),
  onCellPrepared: clearCommentRowPricingCell,
  rowDragging: { allowReordering: isDraft, onDragEnd },
  selection: isDraft ? { mode: 'multiple', showCheckBoxesMode: 'always', selectAllMode: 'allPages' } : { mode: 'none' },
  onSelectionChanged,
  dataActionWidgets: [deleteSelectedToolbarItem],
  addButtons: [{
    label: i18n.t('common.documentRowTypes.comment'),
    icon: 'mdi-comment-plus-outline',
    onClick: () => { showCommentEdit.value = true },
  }],
  addButton: { onClick: () => emit('add-rows', props.invoice) },
})
</script>

<template>
  <div>
    <q-dialog v-model="showCommentEdit" class="dialog-md" persistent>
      <invoice-comment-edit
        v-if="showCommentEdit"
        :invoice="props.invoice"
        @update="onCommentAdded"
        @cancel="showCommentEdit = false"
      />
    </q-dialog>
    <confirm-dialog
      v-model="showBulkDeleteConfirm"
      :message="i18n.t('common.confirmDeleteMessage', { item: `${selectedRowIds.length} ${i18n.t('common.row', selectedRowIds.length).toLocaleLowerCase()}` })"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onBulkDeleteConfirm"
    />
    <dx-data-grid ref="gridRef" v-bind="gridOptions" />
  </div>
</template>
